/**
 * Página de Política de Cookies - Estilo Awwwards
 */

import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Política de Cookies con estilo Awwwards
 * @returns {JSX.Element} Página de política de cookies inmersiva
 */
const CookiesPage = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-end pb-24 md:pb-32 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            <h1 className="hero-title font-baskerville font-bold text-white text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] mb-8 drop-shadow-lg">
              Política de <span className="text-intercasa-yellow">Cookies</span>
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              Información sobre el uso de cookies en nuestro sitio web
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  ¿Qué son las cookies?
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita nuestro sitio web. Estas cookies nos ayudan a mejorar su experiencia de navegación y a proporcionar funcionalidades personalizadas.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.1}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-8">
                  Tipos de cookies que utilizamos
                </h2>

                <div className="space-y-6">
                  <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                    <h3 className="font-baskerville text-2xl font-bold text-intercasa-dark mb-3">
                      Cookies Necesarias
                    </h3>
                    <p className="text-intercasa-gray text-lg leading-relaxed">
                      Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar. Permiten funcionalidades básicas como la navegación por páginas y el acceso a áreas seguras.
                    </p>
                  </div>

                  <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                    <h3 className="font-baskerville text-2xl font-bold text-intercasa-dark mb-3">
                      Cookies Funcionales
                    </h3>
                    <p className="text-intercasa-gray text-lg leading-relaxed">
                      Permiten que el sitio web recuerde sus preferencias (como idioma o región) para proporcionar una experiencia más personalizada.
                    </p>
                  </div>

                  <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                    <h3 className="font-baskerville text-2xl font-bold text-intercasa-dark mb-3">
                      Cookies Analíticas
                    </h3>
                    <p className="text-intercasa-gray text-lg leading-relaxed">
                      Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web mediante la recopilación y el análisis de información de forma anónima.
                    </p>
                  </div>

                  <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                    <h3 className="font-baskerville text-2xl font-bold text-intercasa-dark mb-3">
                      Cookies Publicitarias
                    </h3>
                    <p className="text-intercasa-gray text-lg leading-relaxed">
                      Se utilizan para mostrar anuncios relevantes basados en sus intereses y para medir la efectividad de nuestras campañas publicitarias.
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Cómo gestionar las cookies
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  Puede gestionar sus preferencias de cookies en cualquier momento a través del banner de cookies que aparece en su primera visita al sitio web. También puede configurar su navegador para bloquear o eliminar las cookies, aunque esto puede afectar a la funcionalidad del sitio.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div>
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Más información
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  Para obtener más información sobre cómo procesamos sus datos personales, consulte nuestra Política de Privacidad.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default CookiesPage;
